import { Axios } from "api/axios";
import SearchBox from "Components/SearchBox/SearchBox";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import {
  exportQuotationCSV,
  exportTrashQuotationCSV,
} from "slices/Quotation/thunk";
import QuotationLog from "./QuotationLog/QuotationLog";
import TrashLog from "./TrashLog/TrashLog";

const QuotationContainer = () => {
  const [searchParams] = useSearchParams();
  const savedTab = sessionStorage.getItem("QuotationContainerTab");
  const [tab, setTab] = useState(savedTab ? Number(savedTab) : 1);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.quotation?.app_name
  );
  const quotation_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.quotation?.modules?.quotation
  );

  // console.log("Quotation permissions: --", quotation_permissions);

  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);

  const handleTabChange = (val: number) => {
    setTab(val);
    // setSearchInput("");
    setColValue([]);
    sessionStorage.setItem("QuotationContainerTab", val.toString());

    // Preserve pagination state for both tabs when switching
    const currentQuotationPage = sessionStorage.getItem("quotationPage");
    const currentTrashPage = sessionStorage.getItem("trashPage");

    // Build URL with both pagination states and current tab preserved
    const params = new URLSearchParams();
    if (currentQuotationPage)
      params.append("page_quotation", currentQuotationPage);
    if (currentTrashPage) params.append("page_trash", currentTrashPage);
    params.append("tab", val.toString());

    // Update URL to preserve both tab pagination states and current tab
    navigate(`/quotation?${params.toString()}`, { replace: true });
  };

  // Detect sidebar navigation and set first tab
  useEffect(() => {
    const hasQuotationPage = searchParams.get("page_quotation");
    const hasTrashPage = searchParams.get("page_trash");
    const tabParam = searchParams.get("tab");

    // If no URL parameters, this is sidebar navigation - set first tab and clear sessionStorage
    if (!hasQuotationPage && !hasTrashPage && !tabParam) {
      setTab(1);
      sessionStorage.setItem("QuotationContainerTab", "1");
      // Clear pagination sessionStorage when navigating from sidebar
      sessionStorage.removeItem("quotationPage");
      sessionStorage.removeItem("trashPage");
    } else {
      // If URL has tab parameter, use it directly
      if (tabParam) {
        const tabNumber = parseInt(tabParam);
        if (tabNumber >= 1 && tabNumber <= 2) {
          setTab(tabNumber);
          sessionStorage.setItem("QuotationContainerTab", tabParam);
        }
      } else {
        // If URL has parameters, determine tab based on which parameter exists
        if (hasQuotationPage) {
          setTab(1);
          sessionStorage.setItem("QuotationContainerTab", "1");
        } else if (hasTrashPage) {
          setTab(2);
          sessionStorage.setItem("QuotationContainerTab", "2");
        }
      }
    }
  }, [searchParams]);

  const dispatch = useDispatch<any>();

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await Axios.get("/api/quotation/quotation-dashboard/");
      setDashboardData(response.data.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  //*// Fetch Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]); // Removed tab dependency since permissions should be independent of tab changes

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Handler for card clicks
  const handleCardClick = (status?: string) => {
    // Set tab to 1 (Quotation Log)
    handleTabChange(1);
    
    // Clear search
    setSearchInput("");
    setSearch("");
    
    // Reset pagination in URL
    const params = new URLSearchParams();
    params.append("page_quotation", "0");
    params.append("tab", "1");
    navigate(`/quotation?${params.toString()}`, { replace: true });
    
    // Clear session storage for pagination
    sessionStorage.removeItem("quotationPage");
    
    // Set filters based on status
    if (status) {
      setColValue([{ filterField: "current_status", filterValue: status }]);
    } else {
      // For total quotations, clear all filters
      setColValue([]);
    }
    
    // Trigger reset in QuotationLog
    setResetTrigger((prev) => prev + 1);
  };

  // Handler for Today's Follow Ups button click
  const handleTodaysFollowUpsClick = () => {
    // Set tab to 1 (Quotation Log)
    handleTabChange(1);
    
    // Clear search
    setSearchInput("");
    setSearch("");
    
    // Reset pagination in URL
    const params = new URLSearchParams();
    params.append("page_quotation", "0");
    params.append("tab", "1");
    navigate(`/quotation?${params.toString()}`, { replace: true });
    
    // Clear session storage for pagination
    sessionStorage.removeItem("quotationPage");
    
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Set filter for next_follow_up_date with today's date
    setColValue([
      {
        filterField: "next_follow_up_date",
        filterValue: {
          from_date: todayStr,
          to_date: todayStr,
        },
      },
    ]);
    
    // Trigger reset in QuotationLog
    setResetTrigger((prev) => prev + 1);
  };

  // Handler for Quotation Log tab button click - clears everything
  const handleQuotationLogTabClick = () => {
    // Set tab to 1 (Quotation Log)
    setTab(1);
    sessionStorage.setItem("QuotationContainerTab", "1");
    
    // Clear all filters
    setColValue([]);
    
    // Clear search
    setSearchInput("");
    setSearch("");
    
    // Reset pagination in URL
    const params = new URLSearchParams();
    params.append("page_quotation", "0");
    params.append("tab", "1");
    navigate(`/quotation?${params.toString()}`, { replace: true });
    
    // Clear session storage for pagination
    sessionStorage.removeItem("quotationPage");
    
    // Trigger reset in QuotationLog (this will clear sorting too)
    setResetTrigger((prev) => prev + 1);
  };

  //* Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  //*// updated Export
  const handleExport = async () => {
    try {
      let blob, filename;

      if (tab === 1) {
        // Export normal quotations
        const result = await exportQuotationCSV({
          search,
          filters: colValue,
        });
        blob = result.blob;
        filename = result.filename;
      } else {
        // Export trash quotations
        const result = await exportTrashQuotationCSV({
          search,
          filters: colValue,
        });
        blob = result.blob;
        filename = result.filename;
      }

      // Trigger file download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Quotation</h6>
                    </Col>

                    {/* Search Box */}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    {/* Export & Create Buttons */}
                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <div
                          className="hstack gap-2 btn btn-light"
                          onClick={handleExport}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                          Export
                        </div>
                      </div>
                    </Col>

                    {/* Create Quotation Button */}
                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        {quotation_permissions?.permissions?.Create ===
                          true && (
                          <Button
                            variant="primary"
                            onClick={() => navigate("/add-quotation")}
                          >
                            <i className="bi bi-plus-circle me-1"></i> Create
                            Quotation
                          </Button>
                        )}
                      </div>
                    </Col>

                    {/* Metric Cards */}
                    {dashboardData && (
                      <Row className="mb-0 g-4 mt-0">
                        <Col xl={3} md={6} sm={12}>
                          <div
                            className="dashboard-metric-card h-100 shadow-lg rounded-4 position-relative overflow-hidden border-0"
                            style={{
                              background:
                                "linear-gradient(120deg, #005D97 0%,rgb(4, 152, 202) 100%)",
                              minHeight: 30,
                              transition: "box-shadow 0.2s, transform 0.2s",
                              boxShadow: "0 8px 24px rgba(60, 132, 252, 0.20)",
                              cursor: "pointer",
                            }}
                            onClick={() => handleCardClick()}
                          >
                            <div className="p-2 d-flex flex-row align-items-center justify-content-center h-100 text-center gap-3">
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14 }}
                              >
                                Total Quotations
                              </span>
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14, letterSpacing: 1 }}
                              >
                                {loading ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                ) : (
                                  dashboardData?.summary?.Total_Quotation || 0
                                )}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xl={3} md={6} sm={12}>
                          <div
                            className="dashboard-metric-card h-100 shadow-lg rounded-4 position-relative overflow-hidden border-0"
                            style={{
                              background:
                                "linear-gradient(120deg, rgb(199, 106, 0) 0%,rgb(255, 195, 85) 100%)",
                              minHeight: 30,
                              transition: "box-shadow 0.2s, transform 0.2s",
                              boxShadow: "0 8px 24px rgba(145, 86, 239, 0.13)",
                              cursor: "pointer",
                            }}
                            onClick={() => handleCardClick("Sent")}
                          >
                            <div className="p-2 d-flex flex-row align-items-center justify-content-center h-100 text-center gap-3">
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14 }}
                              >
                                Quotations Sent
                              </span>
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14, letterSpacing: 1 }}
                              >
                                {loading ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                ) : (
                                  dashboardData?.summary?.Sent_count || 0
                                )}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xl={3} md={6} sm={12}>
                          <div
                            className="dashboard-metric-card h-100 shadow-lg rounded-4 position-relative overflow-hidden border-0"
                            style={{
                              background:
                                "linear-gradient(120deg, rgb(4, 180, 39) 0%, rgb(23, 231, 92) 100%)",
                              minHeight: 30,
                              transition: "box-shadow 0.2s, transform 0.2s",
                              boxShadow: "0 8px 24px rgba(50, 201, 122, 0.14)",
                              cursor: "pointer",
                            }}
                            onClick={() => handleCardClick("Won")}
                          >
                            <div className="p-2 d-flex flex-row align-items-center justify-content-center h-100 text-center gap-3">
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14 }}
                              >
                                Won Quotations
                              </span>
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14, letterSpacing: 1 }}
                              >
                                {loading ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                ) : (
                                  dashboardData?.summary?.Won_count || 0
                                )}
                              </span>
                            </div>
                          </div>
                        </Col>
                        <Col xl={3} md={6} sm={12}>
                          <div
                            className="dashboard-metric-card h-100 shadow-lg rounded-4 position-relative overflow-hidden border-0"
                            style={{
                              background:
                                "linear-gradient(120deg, #fd5e53 0%,rgb(252, 140, 112) 100%)",
                              minHeight: 30,
                              transition: "box-shadow 0.2s, transform 0.2s",
                              boxShadow: "0 8px 24px rgba(253, 94, 83, 0.13)",
                              cursor: "pointer",
                            }}
                            onClick={() => handleCardClick("Lost")}
                          >
                            <div className="p-2 d-flex flex-row align-items-center justify-content-center h-100 text-center gap-3">
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14 }}
                              >
                                Lost Quotations
                              </span>
                              <span
                                className="fw-bold text-white"
                                style={{ fontSize: 14, letterSpacing: 1 }}
                              >
                                {loading ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                ) : (
                                  dashboardData?.summary?.Lost_count || 0
                                )}
                              </span>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    )}

                    {/* Tabs */}

                    <div className="d-flex flex-row gap-2 justify-content-between">
                      <div
                        className="mt-4 ml-0 p-0 d-flex gap-2"
                        style={{ marginTop: "-10px" }}
                      >
                        <Button
                          onClick={handleQuotationLogTabClick}
                          className={`btn ${
                            tab === 1 ? "btn-primary" : "btn-secondary"
                          }`}
                        >
                          Quotation Log
                        </Button>
                        <Button
                          onClick={() => handleTabChange(2)}
                          className={`btn ${
                            tab === 2 ? "btn-primary" : "btn-secondary"
                          }`}
                        >
                          Trash
                        </Button>
                      </div>

                      <div
                        className="mt-4 ml-0 p-0 d-flex gap-2"
                        style={{ marginTop: "-10px" }}
                      >
                        <Button 
                          className={`btn btn-primary`}
                          onClick={handleTodaysFollowUpsClick}
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-person-plus me-1"></i> Today's Follow Ups:{" "}
                          {dashboardData?.summary?.Next_Followup_count || 0}
                        </Button>
                      </div>
                    </div>

                    {/* <Col xs={12}>
                      <div className="d-flex flex-wrap gap-3 mt-3">
                        <Button
                          className="btn btn-sm"
                          onClick={() => handleTabChange?.(1)}
                          style={{
                            backgroundColor: tab === 1 ? "#3F7DDA" : "#eef0f7",
                            border: tab === 1 ? "none" : "1px solid #d6d8de",
                            color: tab === 1 ? "#fff" : "#000",
                          }}
                        >
                          Quotation Log
                        </Button>

                        <Button
                          className="btn btn-sm"
                          onClick={() => handleTabChange?.(2)}
                          style={{
                            backgroundColor: tab === 2 ? "#3F7DDA" : "#eef0f7",
                            border: tab === 2 ? "none" : "1px solid #d6d8de",
                            color: tab === 2 ? "#fff" : "#000",
                          }}
                        >
                          Trash
                        </Button>
                      </div>
                    </Col> */}
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  {tab === 1 ? (
                    <QuotationLog
                      handleTabChange={handleTabChange}
                      searchInput={searchInput}
                      setSearch={setSearch}
                      search={search}
                      colValue={colValue}
                      setColValue={setColValue}
                      resetTrigger={resetTrigger}
                      onDashboardRefresh={fetchDashboardData}
                    />
                  ) : (
                    <TrashLog
                      handleTabChange={handleTabChange}
                      searchInput={searchInput}
                      setSearch={setSearch}
                      search={search}
                      colValue={colValue}
                      setColValue={setColValue}
                    />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default QuotationContainer;
